import { useMemo } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import * as LucideIcons from 'lucide-react';
import { MapPin } from 'lucide-react';
import { useTranslation } from '../common/components/LocalizationProvider';

/**
 * Converte kebab-case para PascalCase
 * Ex: "building-2" → "Building2"
 * Ex: "map-pin" → "MapPin"
 */
export const kebabToPascal = (str) => {
  if (!str) return '';
  return str
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');
};

/**
 * Converte PascalCase para kebab-case
 * Ex: "Building2" → "building-2"
 * Ex: "MapPin" → "map-pin"
 */
export const pascalToKebab = (str) => {
  if (!str) return '';
  return str
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2')
    .toLowerCase();
};

/**
 * Obtém o componente Lucide pelo nome (aceita kebab-case ou PascalCase)
 */
export const getLucideIcon = (iconCode) => {
  if (!iconCode) return null;

  // Tenta primeiro como está (PascalCase)
  let IconComponent = LucideIcons[iconCode];

  // Se não encontrar, converte de kebab-case
  if (!IconComponent) {
    const pascalName = kebabToPascal(iconCode);
    IconComponent = LucideIcons[pascalName];
  }

  return IconComponent || null;
};

const PoiMarkerPreview = ({
  icon = null, // { iconType: 'lucide'|'png', iconCode?: string, path?: string }
  color = '#3B82F6',
  name = '',
  size = 'medium', // 'small' | 'medium' | 'large'
  showLabel = true,
  showShadow = true,
}) => {
  const t = useTranslation();

  // Configurações de tamanho - otimizado para balancear visibilidade e densidade
  const sizeConfig = useMemo(() => {
    switch (size) {
      case 'small':
        return { marker: 28, icon: 14, pin: 36 };
      case 'large':
        return { marker: 48, icon: 24, pin: 60 };
      default: // medium - alinhado com 44x54 do mapa
        return { marker: 36, icon: 18, pin: 44 };
    }
  }, [size]);

  // Renderizar ícone interno
  const renderIcon = () => {
    if (!icon) {
      // Ícone padrão
      return <MapPin size={sizeConfig.icon} color="white" />;
    }

    if (icon.iconType === 'lucide') {
      const IconComponent = getLucideIcon(icon.iconCode);
      if (IconComponent) {
        return <IconComponent size={sizeConfig.icon} color="white" />;
      }
      return <MapPin size={sizeConfig.icon} color="white" />;
    }

    if (icon.iconType === 'png' && icon.path) {
      const imgSrc = icon.path.startsWith('/') ? icon.path : `/images/${icon.path}`;
      return (
        <img
          src={imgSrc}
          alt={icon.name || 'POI'}
          style={{
            width: sizeConfig.icon,
            height: sizeConfig.icon,
            objectFit: 'contain',
            filter: 'brightness(0) invert(1)', // Torna branco
          }}
        />
      );
    }

    return <MapPin size={sizeConfig.icon} color="white" />;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
      }}
    >
      {/* Marcador estilo pin */}
      <Box
        sx={{
          position: 'relative',
          width: sizeConfig.marker,
          height: sizeConfig.pin,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {/* Círculo principal - design premium sem borda branca */}
        <Box
          sx={{
            width: sizeConfig.marker,
            height: sizeConfig.marker,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${color} 0%, ${color}DD 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: showShadow ? '0 3px 10px rgba(0,0,0,0.35)' : 'none',
            zIndex: 1,
          }}
        >
          {renderIcon()}
        </Box>

        {/* Ponta do pin (triângulo) */}
        <Box
          sx={{
            width: 0,
            height: 0,
            borderLeft: `${sizeConfig.marker / 4}px solid transparent`,
            borderRight: `${sizeConfig.marker / 4}px solid transparent`,
            borderTop: `${sizeConfig.marker / 2.5}px solid ${color}`,
            marginTop: '-4px',
            filter: showShadow ? 'drop-shadow(0 2px 2px rgba(0,0,0,0.2))' : 'none',
          }}
        />

        {/* Sombra no chão */}
        {showShadow && (
          <Box
            sx={{
              position: 'absolute',
              bottom: -4,
              width: sizeConfig.marker * 0.6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: 'rgba(0,0,0,0.2)',
              filter: 'blur(2px)',
            }}
          />
        )}
      </Box>

      {/* Label */}
      {showLabel && name && (
        <Paper
          elevation={1}
          sx={{
            px: 1,
            py: 0.25,
            borderRadius: 1,
            maxWidth: 120,
          }}
        >
          <Typography
            variant="caption"
            noWrap
            sx={{
              fontWeight: 500,
              display: 'block',
              textAlign: 'center',
            }}
          >
            {name}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

// Preview compacto para uso em listas
export const PoiMarkerMini = ({ icon, color = '#3B82F6', size = 24 }) => {
  const renderIcon = () => {
    if (!icon) {
      return <MapPin size={size * 0.6} color="white" />;
    }

    if (icon.iconType === 'lucide') {
      const IconComponent = getLucideIcon(icon.iconCode);
      if (IconComponent) {
        return <IconComponent size={size * 0.6} color="white" />;
      }
    }

    if (icon.iconType === 'png' && icon.path) {
      const imgSrc = icon.path.startsWith('/') ? icon.path : `/images/${icon.path}`;
      return (
        <img
          src={imgSrc}
          alt=""
          style={{
            width: size * 0.6,
            height: size * 0.6,
            objectFit: 'contain',
            filter: 'brightness(0) invert(1)',
          }}
        />
      );
    }

    return <MapPin size={size * 0.6} color="white" />;
  };

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        flexShrink: 0,
      }}
    >
      {renderIcon()}
    </Box>
  );
};

export default PoiMarkerPreview;
